/**
 * @file Components/Sections/TrendingDestinations.jsx
 * @description Stunning trending destination cards with hover effects,
 * live traveller counts, and category tags. Drives exploration.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Users, ArrowRight, TrendingUp } from 'lucide-react';

const destinations = [
  {
    name: 'Kashmir',
    tagline: 'Paradise on Earth',
    image: 'https://images.unsplash.com/photo-1597074866923-dc0589150458?w=600&q=80',
    travellers: '2.4K',
    rating: 4.9,
    from: '₹8,999',
    tag: 'Trending',
    span: 'col-span-2 row-span-2',
  },
  {
    name: 'Goa',
    tagline: 'Sun, Sand & Serenity',
    image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80',
    travellers: '5.1K',
    rating: 4.7,
    from: '₹4,499',
    tag: 'Most Booked',
    span: 'col-span-1 row-span-1',
  },
  {
    name: 'Rajasthan',
    tagline: 'Royal Heritage Trail',
    image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=400&q=80',
    travellers: '1.8K',
    rating: 4.8,
    from: '₹6,999',
    tag: 'Heritage',
    span: 'col-span-1 row-span-1',
  },
  {
    name: 'Kerala',
    tagline: 'God\'s Own Country',
    image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=400&q=80',
    travellers: '3.2K',
    rating: 4.9,
    from: '₹7,499',
    tag: 'Nature',
    span: 'col-span-1 row-span-1',
  },
  {
    name: 'Ladakh',
    tagline: 'Land of High Passes',
    image: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=400&q=80',
    travellers: '1.2K',
    rating: 4.9,
    from: '₹12,999',
    tag: 'Adventure',
    span: 'col-span-1 row-span-1',
  },
  {
    name: 'Manali',
    tagline: 'Mountain Magic',
    image: 'https://images.unsplash.com/photo-1571401835393-8c5f35328320?w=400&q=80',
    travellers: '4.5K',
    rating: 4.6,
    from: '₹5,499',
    tag: 'Popular',
    span: 'col-span-1 row-span-1',
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const TrendingDestinations = () => (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={20} className="text-[#243185]" />
            <span className="text-sm font-bold text-[#243185] uppercase tracking-wider">Trending Now</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Top Destinations Travellers Love
          </h2>
        </div>
        <Link to="/tours" className="hidden md:flex items-center gap-2 text-[#243185] font-semibold hover:gap-3 transition-all">
          View All <ArrowRight size={18} />
        </Link>
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-60px' }}
        className="grid grid-cols-2 md:grid-cols-4 auto-rows-[200px] gap-4"
      >
        {destinations.map((dest, i) => (
          <motion.div key={dest.name} variants={item} className={i === 0 ? 'col-span-2 row-span-2' : ''}>
            <Link
              to={`/tours?search=${dest.name}`}
              className="group block relative h-full rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500"
            >
              <img
                src={dest.image}
                alt={dest.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              {/* Tag */}
              <span className="absolute top-3 left-3 text-[10px] font-black uppercase bg-white/95 text-[#243185] px-2.5 py-1 rounded-full backdrop-blur-sm">
                {dest.tag}
              </span>

              {/* Travellers Badge */}
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/40 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full">
                <Users size={10} /> {dest.travellers} exploring
              </div>

              {/* Bottom Content */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="text-white font-bold text-lg md:text-xl leading-tight">{dest.name}</h3>
                    <p className="text-white/70 text-xs mt-0.5">{dest.tagline}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-[10px]">from</p>
                    <p className="text-white font-black text-lg">{dest.from}</p>
                  </div>
                </div>

                {/* Hover CTA */}
                <div className="h-0 group-hover:h-10 overflow-hidden transition-all duration-300 mt-0 group-hover:mt-3">
                  <div className="flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white text-sm font-bold py-2 rounded-lg">
                    Explore {dest.name} <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      <Link to="/tours" className="md:hidden flex items-center justify-center gap-2 text-[#243185] font-semibold mt-6">
        View All Destinations <ArrowRight size={18} />
      </Link>
    </div>
  </section>
);

export default TrendingDestinations;
